import Highcharts from 'highcharts/highcharts';
import HighchartsReact, { HighchartsReactRefObject } from 'highcharts-react-official';
import { useRef } from 'react';
import highchartsMore from 'highcharts/highcharts-more';
import { newPalettes } from '../../../themes/newPalette';
import _last from 'lodash/last';
import { renderToString } from 'react-dom/server';
import { ProjectedWealthPathLabel } from '../../atoms/ProjectedWealthPathLabel';
import { GoalPriorityEnum, GoalTypeEnum } from '../../../common/types';
import { UniversalCard } from '../UniversalCard/UniversalCard';
import './styles.scss';
import { Grid, styled } from '@mui/material';
import { formatLabelNumber, labelStyle } from './utils';
// import { ReactComponent as AreaLegendUpper } from '../../../assets/icons/areaRangeUpper.svg';
import { ReactComponent as AlertSVG } from '../../../assets/icons/alertWPC.svg';
import { ReactComponent as CircleSVG } from '../../../assets/icons/circlePWC.svg';
import { ReactComponent as DiamondSVG } from '../../../assets/icons/diamondPWC.svg';
import { formatCurrency } from '../../../utils';
import { Typography } from '../../../components/atoms/Typography/Typography';
import { useTranslation } from 'react-i18next';
import { useCMSContent } from '../../../common/hooks/useCmsContent';
import { iwsGoalTypeMapper } from '../../../common/constants';

// Chart palette for Highcharts (not React-themed)
const chartPalette = newPalettes['advice'];

// Enables the tag <use> from Icon component
Highcharts.AST.allowedTags.push('use');

highchartsMore(Highcharts);

const StyledSpan = styled('div')(({ isAlert, isPDF }: { isAlert: boolean; isPDF: boolean }) => ({
    lineHeight: '18px',
    verticalAlign: 'middle',
    fontWeight: 500,
    fontSize: '13px',
    color: '#595959',
    fontStyle: isAlert ? 'italic' : 'normal',
    marginTop: isPDF ? '-10px' : 0
}));

// Head disclaimer uses the muted text token; StyledSpan (shared with the alert/legend) keeps its own color.
const StyledHeadDisclaimer = styled(StyledSpan)(({ theme }) => ({
    color: theme.palette.semantic.text.muted
}));

const StyledLegendCard = styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '8px',
    flex: '1 0 0',
    alignSelf: 'stretch'
}));

const StyledLegendContainer = styled('div')(({ isPDF }: { isPDF: boolean }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    alignSelf: 'stretch',
    marginBottom: isPDF ? '10px' : '32px'
}));

export const StyledDisclaimerContent = styled(Typography)(({ theme }) => ({
    fontWeight: 400,
    fontSize: theme.spacing(1.5),
    margin: `${theme.spacing(2)} 0`,
    lineHeight: theme.spacing(2),
    color: theme.palette.grey[400],
    padding: '0px 10px'
}));

const StyledLegendLine = styled('div')(() => ({
    borderTop: `3px dotted ${chartPalette.info[200]}`,
    width: '40px',
    margin: '10px 0'
}));

const StyledLegendHeader = styled(Typography)(() => ({
    color: chartPalette.grey[400],
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeightStep: '20px'
}));

const StyledCircleSVG = styled(CircleSVG)<{
    goalPriorityProbability: number;
    goalProbabilityPercentile: number;
}>(({ goalPriorityProbability, goalProbabilityPercentile }) => ({
    color: goalProbabilityPercentile >= goalPriorityProbability ? chartPalette.success[500] : chartPalette.warning[500]
}));

type ProjectedWealthPathData = {
    year: number;
    low: number;
    high: number;
    target: number;
    bankruptcyHigh: number;
    bankruptcyLow: number;
    xAxis?: string;
    tooltipHeader?: string;
};

export type ProjectedWealthPathChartProps = {
    data: ProjectedWealthPathData[];
    plotLine?: {
        type: GoalTypeEnum;
        year: number;
    };
    bankruptcyYear?: number;
    lowPercentile: string;
    highPercentile: string;
    targetPercentile: string;
    goalPriority: GoalPriorityEnum;
    isReadOnly?: boolean; // this is added for PDF rendering
    retirementYear?: number;
    targetGoalAmount?: number;
    projectedGoalAmount?: number;
    goalProbabilityPercentile?: number;
    goalPriorityProbability?: number;
    goalTargetDate?: any;
    isRecomendation?: any;
    isRetirement?: any;
    hideCard?: boolean;
};

export const ProjectedWealthPathChart = ({
    // bankruptcyYear,
    data,
    plotLine,
    isReadOnly = false,
    targetGoalAmount,
    projectedGoalAmount,
    goalProbabilityPercentile,
    highPercentile,
    lowPercentile,
    goalPriorityProbability,
    goalTargetDate,
    isRecomendation,
    isRetirement,
    hideCard = false
}: ProjectedWealthPathChartProps) => {
    const { t } = useTranslation();
    const cmsUtil = useCMSContent();
    const cms_wpcHeadDisclaimer = cmsUtil.getContentAsElement('plan_detail.wealth_projection.part1');
    const chartComponentRef = useRef<HighchartsReactRefObject | null>(null);
    const pointsByIndex = new Map<number, { year: number; xAxis?: string; tooltipHeader?: string }>();
    data?.forEach(({ year, xAxis, tooltipHeader }, index) => pointsByIndex.set(index, { year, xAxis, tooltipHeader }));
    const firstIndex = 0;
    const lastIndex = Math.max((data?.length ?? 0) - 1, 0);
    const defaultTarget = _last(data)?.target;
    const goalTargetYear = new Date(goalTargetDate).getFullYear();
    const highAndLow = data?.map(({ low, high }, index) => [index, low, high]);
    const hightData = highAndLow.map((row) => [row[0], row[2]]);
    const lowData = highAndLow.map((row) => [row[0], row[1]]);
    const redCircleData = isRetirement ? [lastIndex, defaultTarget] : [lastIndex, targetGoalAmount];
    const diamonData = [lastIndex, projectedGoalAmount];
    const redLineData = lowData.map((row, index) => {
        if (index == 0)
            return {
                y: targetGoalAmount,
                x: firstIndex,
                dataLabels: {
                    enabled: true,
                    format: `<label style='font-size: 12px; font-weight: 600;line-height: 16px;letter-spacing: 0.12px;'>${t(
                        'TEXT_TARGET_GOAL'
                    )}: ${formatCurrency(targetGoalAmount)}</label>`,
                    y: -5
                }
            };
        return [row[0], targetGoalAmount];
    });

    const matchedPlotLineIndex = plotLine ? data?.findIndex(({ year }) => year >= plotLine.year) ?? -1 : -1;
    const plotLineIndex = matchedPlotLineIndex > -1 ? matchedPlotLineIndex : lastIndex;

    function truncateAtZero(data: number[][]) {
        let seenZero = false;
        let op = data.map((valarr) => {
            if (valarr[1] === 0) {
                if (!seenZero) {
                    seenZero = true;
                    return [valarr[0], 0];
                }
                return [valarr[0], null]; // hide this and all subsequent points
            } else {
                seenZero = false;
            }
            return valarr;
        });
        return op;
    }
    const series: Highcharts.SeriesOptionsType[] = [
        // {
        //     name: `Median`,
        //     type: 'line',
        //     data: probabilityOfSucess,
        //     zIndex: 2,
        //     color: chartPalette.info[200],
        //     lineWidth: 3,
        //     marker: {
        //         enabled: false
        //     },
        //     animation: !isReadOnly
        // },
        {
            name: `High`,
            type: 'line',
            data: truncateAtZero(hightData),
            dashStyle: 'Dash',
            zIndex: 2,
            color: chartPalette.info[200],
            lineWidth: 2,
            enableMouseTracking: false,
            marker: {
                enabled: false
            },
            animation: !isReadOnly
        },
        {
            name: `Low`,
            type: 'line',
            data: truncateAtZero(lowData),
            zIndex: 2,
            color: chartPalette.info[200],
            lineWidth: 2,
            dashStyle: 'Dash',
            enableMouseTracking: true,
            marker: {
                symbol: 'circle',
                enabled: false
            },
            states: {
                hover: {
                    lineWidthPlus: 0 // Prevent line from becoming thicker on hover
                }
            },
            animation: !isReadOnly
        },

        {
            name: 'High & Low',
            type: 'arearange',
            data: highAndLow,
            color: chartPalette.info[100],
            lineWidth: 0,
            animation: !isReadOnly,
            marker: {
                enabled: false
            },
            zIndex: 0,
            states: {
                inactive: {
                    opacity: 1
                },
                hover: {
                    opacity: 1,
                    enabled: false
                }
            }
        },
        {
            type: 'scatter',
            name: 'Marker inside area',
            data: [
                // x, y position (somewhere between low and high)
                redCircleData
            ],
            clip: false,
            marker: {
                symbol: 'circle',
                radius: 6,
                ...(goalPriorityProbability && goalProbabilityPercentile
                    ? {
                          fillColor:
                              goalProbabilityPercentile >= goalPriorityProbability
                                  ? chartPalette.success[500]
                                  : chartPalette.warning[500]
                      }
                    : {})
            },
            tooltip: {
                pointFormat: 'Custom Marker: <b>{point.y}°C</b>'
            },
            animation: !isReadOnly,
            visible: isRetirement && !isRecomendation ? false : true
        }
    ];

    if (!isRetirement) {
        series.push(
            {
                name: `tagetAmount`,
                type: 'line',
                data: redLineData,
                zIndex: 2,
                ...(goalPriorityProbability && goalProbabilityPercentile
                    ? {
                          color:
                              goalProbabilityPercentile >= goalPriorityProbability
                                  ? chartPalette.success[500]
                                  : chartPalette.warning[500]
                      }
                    : {}),
                lineWidth: 2,
                dashStyle: 'Dot',
                enableMouseTracking: false,
                marker: {
                    enabled: false
                },
                animation: !isReadOnly,
                dataLabels: { enabled: false }
            },
            {
                type: 'scatter',
                name: 'Marker inside area',
                data: [
                    // x, y position (somewhere between low and high)
                    diamonData
                ],
                clip: false,
                marker: {
                    symbol: 'diamond',
                    radius: 6,
                    fillColor: '#3769FF'
                },
                tooltip: {
                    pointFormat: 'Custom Marker: <b>{point.y}°C</b>'
                },
                animation: !isReadOnly
            }
        );
    }

    // if (bankruptcyYear) {
    //     series.push(
    //         {
    //             name: 'No Income',
    //             type: 'arearange',
    //             data: highAndLowBankruptcy,
    //             color: chartPalette.berry[100],
    //             lineWidth: 0,
    //             marker: {
    //                 enabled: false
    //             },
    //             zIndex: 0,
    //             states: {
    //                 inactive: {
    //                     opacity: 1
    //                 },
    //                 hover: {
    //                     opacity: 1,
    //                     enabled: false
    //                 }
    //             }
    //         },
    //         {
    //             name: 'No Income',
    //             showInLegend: false,
    //             type: 'line',
    //             data: bankruptcyLine,
    //             zIndex: 2,
    //             color: chartPalette.berry[400],
    //             lineWidth: 3,
    //             marker: {
    //                 enabled: false
    //             }
    //         }
    //     );
    // }

    const options: Highcharts.Options = {
        chart: {
            style: {
                fontFamily: 'TT Commons Pro',
                overflow: 'visible'
            },
            animation: !isReadOnly,
            spacingTop: 20,
            spacingRight: 20,
            height: isReadOnly ? '38%' : 'inherit'
        },
        title: {
            text: ''
        },
        xAxis: {
            // custom tooltip and last label overflows for width > 98%
            width: '98%',
            crosshair: {
                dashStyle: 'Solid',
                zIndex: 3
            },
            type: 'linear',
            lineWidth: 0,
            left: 70,
            minPadding: 0,
            maxPadding: 0,
            min: firstIndex,
            max: lastIndex,
            allowDecimals: false,
            tickColor: 'rgba(0,0,0,0.5)',
            tickPosition: 'outside',
            labels: {
                distance: 20,
                style: labelStyle,
                formatter: ({ value }) => {
                    const point = pointsByIndex.get(Number(value));
                    if (!point) return '';
                    return point.xAxis ?? point.year.toString();
                }
            },

            ...(!!plotLine && plotLine?.type !== iwsGoalTypeMapper.income && {
                plotLines: [
                    {
                        dashStyle: 'Dash',
                        color: chartPalette.grey[400],
                        width: 1,
                        value: plotLineIndex,
                        zIndex: 10,
                        label: {
                            align: 'center',
                            rotation: 360,
                            useHTML: true,
                            verticalAlign: 'top',
                            y: -10,
                            x: -10,
                            formatter: () => {
                                return renderToString(
                                    <>
                                        <div className="plot-title">{t('DRAWDOWN_BEGIN')}</div>
                                        <ProjectedWealthPathLabel type={plotLine.type} isPDF={isReadOnly} />
                                    </>
                                );
                            }
                        }
                    }
                ]
            })
        },
        yAxis: {
            allowDecimals: false,
            crosshair: false,
            gridLineWidth: 0,
            tickmarkPlacement: 'on',
            tickWidth: 1,
            tickColor: 'rgba(0,0,0,0.5)',
            tickPosition: 'outside',
            title: {
                text: null
            },
            labels: {
                distance: 20,
                style: labelStyle,
                formatter: function () {
                    return formatLabelNumber(this.value as number);
                }
            },
            minPadding: 1,
            maxPadding: 0
        },
        tooltip: {
            shared: true,
            outside: false,
            useHTML: true,
            followPointer: false,
            followTouchMove: false,
            backgroundColor: 'transparent',
            borderWidth: 0,

            formatter: function () {
                const index = Number(this.x);
                const currentAreaData = data?.[index];
                const point = pointsByIndex.get(index);
                const { high = 0, low = 0, target = 0 } = currentAreaData || {};

                const showHigh = high > 0;
                const showLow = low > 0;

                // TODO: Migrate it to a component
                return `
                <div class="tooltip-chart-card">
                    <strong class="title">${point?.tooltipHeader || point?.year || ''}</strong>
                    <!--<strong class="subtitle">PROJECTIONS</strong>-->
                    <div>
                        <div class="legend">
                        <div class="left">
                            <!--<div
                            class="bar"
                            style="border-bottom:3px dashed ${chartPalette.teal[300]}"
                            ></div>-->
                        ${
                            showHigh
                                ? `<span class="name">${highPercentile}</span>
                            <span class="name">${t('LIKELY_TEXT')}</span>
                        </div>
                        <strong class="amount">${formatCurrency(high)}</strong>`
                                : ''
                        }
                        </div>

                        <!--<div class="legend">
                        <div class="left">
                            <div
                            class="line"
                            style="background: ${chartPalette.info[200]}"
                            ></div>
                            <span class="name">${t('MEDIAN')}</span>
                            
                        </div>

                        <strong class="amount">${formatCurrency(target)}</strong>
                        </div>-->

                        <div class="legend" style="margin-bottom: 0">
                        <div class="left">
                             <!--<div
                                class="low-bar"
                                ></div>
                                <span class="name">Low </span>
                                -->
                            ${
                                showLow
                                    ? ` <span class="name">${lowPercentile}</span>
                            <span class="name">${t('LIKELY_TEXT')}</span>
                        </div>
                        <strong class="amount">${formatCurrency(low)}</strong>`
                                    : ''
                            }
                    </div>
                </div>
                    `;
            }
        },
        plotOptions: {
            series: {
                pointStart: firstIndex
            }
        },
        legend: {
            enabled: false,
            align: isReadOnly ? 'left' : 'right',
            verticalAlign: 'top',
            itemMarginBottom: 25,
            symbolHeight: 20,
            symbolWidth: 25,
            itemStyle: {
                fontSize: '16px',
                fontWeight: '500',
                lineHeight: '20px'
            }
        },
        series,
        credits: {
            enabled: false
        }
    };

    const chartContent = (
        <>
            <StyledHeadDisclaimer isAlert={false} isPDF={isReadOnly}>
                {cms_wpcHeadDisclaimer}
            </StyledHeadDisclaimer>
            <CustomLegend
                isPDF={isReadOnly}
                pessimisticPercentile={lowPercentile}
                optimisticPercentile={highPercentile}
                goalProbabilityPercentile={goalProbabilityPercentile}
                goalTarget={isRetirement ? defaultTarget : targetGoalAmount}
                goalTargetDate={goalTargetYear}
                goalPriorityProbability={goalPriorityProbability}
                goalProjectedAmount={projectedGoalAmount}
                isRecomendation={isRecomendation}
                isRetirement={isRetirement}
                isIncome={plotLine?.type === iwsGoalTypeMapper.income}
            />
            <HighchartsReact highcharts={Highcharts} options={options} ref={chartComponentRef} />
            {!isReadOnly && (
                <StyledDisclaimerContent
                    label={cmsUtil.getContentAsString('plan_detail.wealth_projection.disclaimer')}
                />
            )}
        </>
    );

    return (
        <Grid marginTop={3}>
            {isReadOnly || hideCard ? (
                chartContent
            ) : (
                <UniversalCard
                    header={t('LABEL_WEALTH_PROJECTIONS')}
                    showInfoToolTip={false}
                    Infomsg={'PROJECTED_WEALTH_PATH_INFO'}
                >
                    {chartContent}
                </UniversalCard>
            )}
        </Grid>
    );
};

export interface CustomLegendProps {
    isPDF: boolean;
    pessimisticPercentile: number | string;
    optimisticPercentile: number | string;
    goalProbabilityPercentile: any;
    goalTarget: any;
    goalTargetDate: any;
    goalPriorityProbability: any;
    goalProjectedAmount: any;
    isRecomendation?: any;
    isRetirement?: any;
    isIncome?: any;
}

const CustomLegend = ({
    isPDF,
    pessimisticPercentile,
    optimisticPercentile,
    goalProbabilityPercentile,
    goalTarget,
    goalTargetDate,
    goalPriorityProbability,
    goalProjectedAmount,
    isRecomendation,
    isRetirement,
    isIncome
}: CustomLegendProps) => {
    const cmsUtil = useCMSContent();

    let wealthContent: Record<string, { header: string; content: string }> = {
        line: {
            header: cmsUtil.getContentAsElement('plan_detail.wealth_projection.line.header'),
            content: cmsUtil.getContentAsElement('plan_detail.wealth_projection.line.body', {
                value1: pessimisticPercentile,
                value2: optimisticPercentile
            })
        },
        circle: {
            header: cmsUtil.getContentAsElement('plan_detail.wealth_projection.wcircle.header', {
                value1: goalProbabilityPercentile
            }),
            content: cmsUtil.getContentAsElement('plan_detail.wealth_projection.wcircle.body', {
                value1: goalProbabilityPercentile >= goalPriorityProbability ? 'teal' : 'orange',
                value2: formatCurrency(goalTarget),
                value3: goalProbabilityPercentile,
                value4: goalTargetDate
            })
        },
        diamond: {
            header: cmsUtil.getContentAsElement('plan_detail.wealth_projection.wdiamond.header', {
                value1: goalPriorityProbability,
                value2: formatCurrency(goalProjectedAmount)
            }),
            content: cmsUtil.getContentAsElement('plan_detail.wealth_projection.wdiamond.body', {
                value1: formatCurrency(goalProjectedAmount),
                value2: goalPriorityProbability,
                value3: goalTargetDate,
                value4: goalPriorityProbability
            })
        }
    };

    let retirementGoalContent: Record<string, { header: string; content: string }> = {
        line: {
            header: cmsUtil.getContentAsElement('plan_detail.wealth_projection.line.header'),
            content: cmsUtil.getContentAsElement('plan_detail.wealth_projection.line.body', {
                value1: pessimisticPercentile,
                value2: optimisticPercentile
            })
        }
    };

    if (isRecomendation) {
        retirementGoalContent['circle'] = {
            header: cmsUtil.getContentAsElement('plan_detail.wealth_projection.rcircle.header', {
                value1: goalProbabilityPercentile
            }),
            content: cmsUtil.getContentAsElement(
                isIncome
                    ? 'plan_detail.wealth_projection.incomecircle.body'
                    : 'plan_detail.wealth_projection.rcircle.body',
                {
                    value1: goalProbabilityPercentile >= goalPriorityProbability ? 'teal' : 'orange',
                    value2: formatCurrency(goalTarget),
                    value3: goalProbabilityPercentile,
                    value4: goalTargetDate
                }
            )
        };
    } else {
        retirementGoalContent['alert'] = {
            header: '',
            content: cmsUtil.getContentAsElement(
                isIncome
                    ? 'plan_detail.wealth_projection.incomealert.body'
                    : 'plan_detail.wealth_projection.alert.body',
                {
                    value1: goalPriorityProbability
                }
            )
        };
    }

    let dynamicContent = isRetirement ? retirementGoalContent : wealthContent;
    // eslint-disable-next-line no-unused-vars
    let innerComp = Object.keys(dynamicContent).map((key, index) => {
        return (
            <StyledLegendCard key={`${index}_LC`}>
                <Grid height={'18px'} marginTop={'16px'}>
                    {key == 'line' ? <StyledLegendLine></StyledLegendLine> : null}
                    {key == 'circle' ? (
                        <StyledCircleSVG
                            goalPriorityProbability={goalPriorityProbability}
                            goalProbabilityPercentile={goalProbabilityPercentile}
                        />
                    ) : null}
                    {key == 'diamond' ? <DiamondSVG /> : null}
                </Grid>
                <Grid display={'flex'} alignItems={'flex-start'} gap={'8px'} alignSelf={'stretch'}>
                    <Grid display={key == 'alert' ? 'block' : 'none'}>
                        <AlertSVG />
                    </Grid>
                    <Grid>
                        {dynamicContent[key].header != '' ? (
                            <Grid>
                                <StyledLegendHeader>{dynamicContent[key].header}</StyledLegendHeader>
                            </Grid>
                        ) : null}
                        <Grid>
                            <StyledSpan isAlert={key == 'alert'} isPDF={false}>
                                {dynamicContent[key].content}
                            </StyledSpan>
                        </Grid>
                    </Grid>
                </Grid>
            </StyledLegendCard>
        );
    });
    return (
        // <Grid container justifyContent={`${isPDF ? 'flex-start' : 'flex-end'}`} className="custom-legend">
        //     <Grid className="left">
        //         <Grid className="line" style={{ background: chartPalette.info[200] }}></Grid>
        //         <StyledSpan>Median</StyledSpan>
        //     </Grid>
        //     <Grid className="left">
        //         <Grid className="grid">
        //             <AreaLegendUpper />
        //             <AreaLegendLower />
        //         </Grid>
        //         <StyledSpan>High & Low</StyledSpan>
        //     </Grid>
        // </Grid>
        <StyledLegendContainer isPDF={isPDF}>
            {/* inner cards */}
            {innerComp}
        </StyledLegendContainer>
    );
};
